/**
 * Locations Widget - Embeddable Bundle Entry Point
 *
 * Creates a standalone widget embedded in a shadow DOM for style isolation.
 * Follows the same pattern as the whats-happening widget.
 *
 * Embed:
 *   <div id="locations-root"></div>
 *   <script src="https://widgets.woodsidebible.org/locations/widget.js"></script>
 *
 * Optional attributes on the root:
 *   data-live-only="true"   open with the "Live now" chip on (the /live page);
 *                           visitors can still turn it off. Not the default,
 *                           /locations wants every campus.
 *
 * The location sheets portal into a SECOND shadow root that hangs off
 * document.body, not into the widget's own (same as the group finder). On
 * woodsidebible.org the widget sits inside .container-wrap and a WPBakery
 * column, both `position: relative; z-index: 10`, so a fixed overlay rendered
 * in the widget's root is trapped in that stacking context and the theme's
 * fixed header (z-index 9999) and page title band paint over the backdrop and
 * the sheet.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { LocationsFinder } from '../src/components/LocationsFinder';
import type { LocationsFinderProps } from '../src/components/LocationsFinder';
import { PortalContainerProvider } from '@church/nextjs-ui/bottom-sheet';
import styleText from '../src/app/globals.css?inline';

interface WidgetConfig extends LocationsFinderProps {
  containerId?: string;
}

declare global {
  interface Window {
    LOCATIONS_CONFIG?: WidgetConfig;
    LocationsWidget?: LocationsWidgetInstance;
  }
}

const OVERLAY_HOST_ID = 'locations-overlay';
// Above the theme's fixed header (#header-outer is z-index 9999 on woodsidebible.org)
const OVERLAY_Z_INDEX = '2147483000';

class LocationsWidgetInstance {
  private root: ReturnType<typeof createRoot> | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private overlayHost: HTMLElement | null = null;
  private config: WidgetConfig;

  constructor(config: WidgetConfig = {}) {
    this.config = {
      containerId: 'locations-root',
      ...config,
    };
  }

  init() {
    const container = document.getElementById(this.config.containerId!);
    if (!container) {
      console.error(`Locations: Container #${this.config.containerId} not found`);
      return;
    }

    // Re-use existing shadow root if the widget was already initialized
    if (container.shadowRoot) {
      this.shadowRoot = container.shadowRoot;
      const existingApp = this.shadowRoot.getElementById('locations-app');
      if (existingApp) {
        console.log('Locations Widget: already initialized, skipping');
        return;
      }
    } else {
      this.shadowRoot = container.attachShadow({ mode: 'open' });
    }

    const mountPoint = document.createElement('div');
    mountPoint.id = 'locations-app';
    this.shadowRoot.appendChild(mountPoint);

    // Portal container for the sheets: a body-level shadow root with a
    // top-level z-index, so the overlay stacks against the page rather than
    // inside the widget's ancestors. It gets the same stylesheet.
    const portalContainer = this.mountOverlayHost();

    this.injectFont();
    const css = this.buildStyles();
    this.appendStyle(this.shadowRoot, css);
    this.appendStyle(portalContainer.getRootNode() as ShadowRoot, css);
    this.preventScrollLock();

    // Read config from data attributes
    const googleMapsApiKey =
      this.config.googleMapsApiKey ?? container.dataset.googleMapsApiKey ?? undefined;
    const viewLocationBaseUrl =
      this.config.viewLocationBaseUrl ?? container.dataset.viewLocationBaseUrl ?? undefined;
    const selectedCongregationId =
      this.config.selectedCongregationId ??
      (container.dataset.selectedCongregationId
        ? Number(container.dataset.selectedCongregationId)
        : null);
    // data-live-only, data-live-only="true" or "1" turn it on; "false" and "0" leave it off
    const liveOnlyAttr = container.dataset.liveOnly;
    const liveOnly =
      this.config.liveOnly ??
      (liveOnlyAttr != null && !['false', '0'].includes(liveOnlyAttr.trim().toLowerCase()));

    this.root = createRoot(mountPoint);
    this.root.render(
      <React.StrictMode>
        <PortalContainerProvider container={portalContainer}>
          <LocationsFinder
            apiBaseUrl={this.config.apiBaseUrl}
            googleMapsApiKey={googleMapsApiKey}
            viewLocationBaseUrl={viewLocationBaseUrl}
            selectedCongregationId={selectedCongregationId}
            liveOnly={liveOnly}
          />
        </PortalContainerProvider>
      </React.StrictMode>
    );
  }

  /**
   * Prevent scroll locking from causing layout shift on the host page.
   * The bottom sheet component locks body scroll when opening — since our
   * sheet lives in shadow DOM, the host page should not be affected.
   */
  private preventScrollLock() {
    if ((window as any).__LOCATIONS_WIDGET_SCROLL_LOCK_PREVENTED__) return;

    const cleanElement = (el: HTMLElement) => {
      if (el.hasAttribute('data-scroll-locked')) {
        el.removeAttribute('data-scroll-locked');
      }
      if (el.style.getPropertyValue('overflow') === 'hidden') {
        el.style.removeProperty('overflow');
      }
      if (el.style.getPropertyValue('padding-right')) {
        el.style.removeProperty('padding-right');
      }
      if (el.style.getPropertyValue('margin-right')) {
        el.style.removeProperty('margin-right');
      }
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-scroll-locked') {
          cleanElement(document.body);
          cleanElement(document.documentElement);
        }
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-scroll-locked'],
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-scroll-locked'],
    });

    (window as any).__LOCATIONS_WIDGET_SCROLL_LOCK_PREVENTED__ = true;
  }

  private injectFont() {
    const fontId = 'locations-montserrat';
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&family=Bebas+Neue&display=swap';
      document.head.appendChild(link);
    }
  }

  /**
   * The overlay host: a zero-size element at the end of <body> with its own
   * shadow root, positioned with a top-level z-index so fixed overlays inside
   * it stack against the page rather than inside the widget's ancestors.
   */
  private mountOverlayHost(): HTMLElement {
    let host = document.getElementById(OVERLAY_HOST_ID);
    if (!host) {
      host = document.createElement('div');
      host.id = OVERLAY_HOST_ID;
      host.style.cssText = `position:relative;z-index:${OVERLAY_Z_INDEX};width:0;height:0;`;
      document.body.appendChild(host);
    }
    const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    root.replaceChildren();
    const container = document.createElement('div');
    container.id = 'locations-portals';
    root.appendChild(container);
    this.overlayHost = host;
    return container;
  }

  private appendStyle(root: ShadowRoot, css: string) {
    const style = document.createElement('style');
    style.textContent = css;
    root.appendChild(style);
  }

  private buildStyles(): string {
    const resetCSS = `
      *, *::before, *::after { box-sizing: border-box; }

      :host {
        display: block;
        width: 100%;
        font-family: 'Montserrat', ui-sans-serif, system-ui, sans-serif;
      }

      #locations-app {
        width: 100%;
        min-width: 100%;
        max-width: 100%;
      }

      #locations-portals, #locations-portals * {
        font-family: 'Montserrat', ui-sans-serif, system-ui, sans-serif;
      }

      :host {
        --tw-border-spacing-x: 0;
        --tw-border-spacing-y: 0;
        --tw-translate-x: 0;
        --tw-translate-y: 0;
        --tw-rotate: 0;
        --tw-skew-x: 0;
        --tw-skew-y: 0;
        --tw-scale-x: 1;
        --tw-scale-y: 1;
        --tw-pan-x: ;
        --tw-pan-y: ;
        --tw-pinch-zoom: ;
        --tw-scroll-snap-strictness: proximity;
        --tw-gradient-from-position: ;
        --tw-gradient-via-position: ;
        --tw-gradient-to-position: ;
        --tw-ordinal: ;
        --tw-slashed-zero: ;
        --tw-numeric-figure: ;
        --tw-numeric-spacing: ;
        --tw-numeric-fraction: ;
        --tw-ring-inset: ;
        --tw-ring-offset-width: 0px;
        --tw-ring-offset-color: #fff;
        --tw-ring-color: rgb(59 130 246 / 0.5);
        --tw-ring-offset-shadow: 0 0 #0000;
        --tw-ring-shadow: 0 0 #0000;
        --tw-shadow: 0 0 #0000;
        --tw-shadow-colored: 0 0 #0000;
        --tw-inset-shadow: 0 0 #0000;
        --tw-inset-ring-shadow: 0 0 #0000;
        --tw-border-style: solid;
        --tw-blur: ;
        --tw-brightness: ;
        --tw-contrast: ;
        --tw-grayscale: ;
        --tw-hue-rotate: ;
        --tw-invert: ;
        --tw-saturate: ;
        --tw-sepia: ;
        --tw-drop-shadow: ;
        --tw-backdrop-blur: ;
        --tw-backdrop-brightness: ;
        --tw-backdrop-contrast: ;
        --tw-backdrop-grayscale: ;
        --tw-backdrop-hue-rotate: ;
        --tw-backdrop-invert: ;
        --tw-backdrop-opacity: ;
        --tw-backdrop-saturate: ;
        --tw-backdrop-sepia: ;
        --tw-contain-size: ;
        --tw-contain-layout: ;
        --tw-contain-paint: ;
        --tw-contain-style: ;
      }
    `;

    // Replace :root → :host and body → #locations-app for shadow DOM
    let shadowCSS = styleText
      .replace(/:root\b/g, ':host')
      .replace(/\bbody\b/g, '#locations-app')
      .replace(/(#locations-app\s*{[^}]*?)overflow-y:\s*scroll\s*!important;?/g, '$1');

    return resetCSS + '\n' + shadowCSS;
  }

  destroy() {
    this.root?.unmount();
    this.root = null;
    this.overlayHost?.remove();
    this.overlayHost = null;
  }
}

/** Read congregation ID from cookies (new format first, then legacy fallback) */
function getCongregationIdFromCookie(): number | null {
  if (typeof document === 'undefined') return null;

  // Try new cookie first
  try {
    const match = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith('woodside-selected-campus='));
    if (match) {
      const value = decodeURIComponent(match.split('=').slice(1).join('='));
      const data = JSON.parse(value);
      if (data.congregationId) return Number(data.congregationId);
    }
  } catch {
    /* fall through to legacy */
  }

  // Legacy cookie fallback
  try {
    const match = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith('tbx-ws__selected-location='));
    if (!match) return null;
    const value = decodeURIComponent(match.split('=').slice(1).join('='));
    const data = JSON.parse(atob(value));
    return data.location_id ? Number(data.location_id) : null;
  } catch {
    return null;
  }
}

if (typeof window !== 'undefined') {
  const config: WidgetConfig = {
    apiBaseUrl: 'https://api.woodsidebible.org',
    googleMapsApiKey: 'AIzaSyBzf7AATUxLctt3O69OqPVbi3owcCWp17Y',
    containerId: 'locations-root',
    selectedCongregationId: getCongregationIdFromCookie(),
    ...(window.LOCATIONS_CONFIG || {}),
  };

  if (!window.LocationsWidget) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        const widget = new LocationsWidgetInstance(config);
        widget.init();
        window.LocationsWidget = widget;
      });
    } else {
      const widget = new LocationsWidgetInstance(config);
      widget.init();
      window.LocationsWidget = widget;
    }
  }
}

export default LocationsWidgetInstance;
